import { config, redirectUri } from "./config";

/**
 * Sign-in against the Cognito hosted UI, authorization code flow with PKCE.
 *
 * **Why the hosted UI rather than calling Cognito directly.** Three reasons, and the third is the one
 * that decided it:
 *
 * 1. The app client has no secret and `ExplicitAuthFlows` is unset, so `USER_PASSWORD_AUTH` and SRP
 *    are not available to it. The OAuth code flow is what the client is actually configured for.
 * 2. Sign-up, email verification, password rules and password reset all come for free and correctly.
 *    None of that is interesting to write and all of it is easy to get subtly wrong.
 * 3. **Google drops in with no change here.** Adding the identity provider in Terraform makes a
 *    Google button appear on the hosted UI; this file does not learn about it. That is the whole
 *    reason the deferred Google work costs nothing today.
 *
 * PKCE rather than an implicit flow because a browser cannot keep a secret: the code verifier never
 * leaves this origin, and the authorization code alone is useless without it.
 */

const STORAGE = {
  /** Survives the redirect to Cognito and back, which is why it is session rather than memory. */
  verifier: "gej.pkce.verifier",
  state: "gej.pkce.state",
  tokens: "gej.tokens",
} as const;

export interface Tokens {
  /**
   * **The credential for the API.** The authorizer verifies it by calling Cognito's `GetUser`, which
   * only accepts an access token — an id token is rejected. Sending the wrong one produces a 403 that
   * looks like a permissions problem and is not.
   */
  accessToken: string;
  /** Carries name and email for display. Never sent to the API. */
  idToken: string;
  refreshToken?: string;
  /** Epoch milliseconds. */
  expiresAt: number;
}

export interface Profile {
  sub: string;
  email?: string;
  name?: string;
}

// --- PKCE ---------------------------------------------------------------------------------------

function randomUrlSafe(bytes = 32): string {
  const raw = new Uint8Array(bytes);
  crypto.getRandomValues(raw);
  return base64UrlEncode(raw);
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

// --- Redirects ----------------------------------------------------------------------------------

/** Sends the browser to the hosted UI. `screen` picks the sign-in or the sign-up tab. */
export async function beginSignIn(screen: "login" | "signup" = "login"): Promise<void> {
  const verifier = randomUrlSafe();
  const state = randomUrlSafe(16);
  sessionStorage.setItem(STORAGE.verifier, verifier);
  sessionStorage.setItem(STORAGE.state, state);

  const url = new URL(`https://${config.cognito.hostedUiDomain}/${screen}`);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", config.cognito.clientId);
  url.searchParams.set("redirect_uri", redirectUri());
  url.searchParams.set("scope", config.cognito.scopes.join(" "));
  url.searchParams.set("state", state);
  url.searchParams.set("code_challenge", await challengeFor(verifier));
  url.searchParams.set("code_challenge_method", "S256");
  window.location.assign(url.toString());
}

/**
 * The OAuth callback parameters, read **once at module load** and never again.
 *
 * This is the fix for a real and thoroughly confusing bug. `completeSignIn` used to read
 * `window.location.search` when it ran, which is inside a React effect — and another effect, declared
 * earlier in `App`, writes the current screen into the URL with
 * `replaceState(null, "", window.location.pathname + hash)`. `pathname` carries no query string, so that
 * call **deleted `?code=…&state=…` before sign-in could read it.** Effects run in declaration order, so
 * the authorization code was destroyed a few microseconds before it was needed, every single time.
 *
 * What that looked like from the outside was nothing like the cause: sign-in appeared to bounce straight
 * back to the sign-in screen, and trying again showed Cognito's "You're still signed in. Sign in again?"
 * page — because Cognito had authenticated perfectly and held a session, while the app had thrown away
 * the evidence. It broke email and password sign-in exactly as much as Google.
 *
 * Capturing at module load makes it structurally impossible for any later code to interfere, whatever it
 * does to the address bar. `takeInviteCode` in `App.tsx` already did this, for this reason, with a
 * comment saying so — the same hazard, spotted for the invite code and then recreated for the auth code.
 *
 * The `window` guard is for the test runner, which has no DOM: without it, importing this module — or
 * anything that imports it, which is most of the app — throws at collection time.
 */
const callback = readCallback(typeof window === "undefined" ? "" : window.location.search);

/** Pulls what matters out of an OAuth callback query string. Exported so it can be tested. */
export function readCallback(search: string): {
  code: string | null;
  state: string | null;
  error: string | null;
  errorDescription: string | null;
} {
  const params = new URLSearchParams(search);
  return {
    code: params.get("code"),
    state: params.get("state"),
    error: params.get("error"),
    errorDescription: params.get("error_description"),
  };
}

/**
 * Completes the flow if this load is a redirect back from Cognito. Returns null on an ordinary load.
 *
 * Removes the code from the address bar on success, so a refresh does not attempt to redeem a code
 * that has already been spent — Cognito allows a code exactly once.
 */
export async function completeSignIn(): Promise<Tokens | null> {
  const { code, state: returnedState, error, errorDescription } = callback;

  if (error) {
    clearUrl();
    throw new Error(errorDescription ?? error);
  }
  if (!code) return null;

  const verifier = sessionStorage.getItem(STORAGE.verifier);
  const expectedState = sessionStorage.getItem(STORAGE.state);
  sessionStorage.removeItem(STORAGE.verifier);
  sessionStorage.removeItem(STORAGE.state);
  clearUrl();

  if (!verifier) throw new Error("Sign-in could not be completed. Please try again.");
  // The state check is what makes a forged callback useless, so a mismatch is a hard failure rather
  // than something to work around.
  if (!expectedState || returnedState !== expectedState) {
    throw new Error("Sign-in state did not match. Please try again.");
  }

  const tokens = await exchange({
    grant_type: "authorization_code",
    client_id: config.cognito.clientId,
    code,
    redirect_uri: redirectUri(),
    code_verifier: verifier,
  });
  store(tokens);
  return tokens;
}

export function signOut(): void {
  localStorage.removeItem(STORAGE.tokens);
  const url = new URL(`https://${config.cognito.hostedUiDomain}/logout`);
  url.searchParams.set("client_id", config.cognito.clientId);
  url.searchParams.set("logout_uri", redirectUri());
  window.location.assign(url.toString());
}

/**
 * Discards the stored tokens without visiting Cognito.
 *
 * Needed because a token can be valid and still unusable. **A refresh preserves the scopes the token
 * was originally issued with**, so a token minted before a scope was added to the app client can never
 * be repaired by refreshing it — the only way forward is a new authorization. Keeping it would mean
 * refreshing a token the API will always reject, forever.
 *
 * Deliberately does not redirect: the caller shows the sign-in screen, and because the Cognito session
 * cookie usually survives, signing in again is one tap with no password.
 */
export function forgetTokens(): void {
  localStorage.removeItem(STORAGE.tokens);
}

// --- Tokens -------------------------------------------------------------------------------------

export function storedTokens(): Tokens | null {
  const raw = localStorage.getItem(STORAGE.tokens);
  if (!raw) return null;
  try {
    const tokens = JSON.parse(raw) as Tokens;
    return tokens.accessToken ? tokens : null;
  } catch {
    return null;
  }
}

/**
 * A valid access token, refreshed if it is close to expiring.
 *
 * Refreshed an hour early is wrong and a minute early is fragile, so this uses thirty seconds of
 * margin — enough to cover a request in flight when the clock rolls over.
 *
 * Returns null when there is nothing usable, which callers treat as "signed out" rather than as an
 * error: an expired session is an ordinary thing to happen overnight.
 */
export async function freshAccessToken(): Promise<string | null> {
  const tokens = storedTokens();
  if (!tokens) return null;
  if (Date.now() < tokens.expiresAt - 30_000) return tokens.accessToken;

  if (!tokens.refreshToken) {
    localStorage.removeItem(STORAGE.tokens);
    return null;
  }
  try {
    const refreshed = await exchange({
      grant_type: "refresh_token",
      client_id: config.cognito.clientId,
      refresh_token: tokens.refreshToken,
    });
    // A refresh response does not return the refresh token again, so the existing one is carried over
    // or the next refresh has nothing to work with.
    const merged: Tokens = { ...refreshed, refreshToken: refreshed.refreshToken ?? tokens.refreshToken };
    store(merged);
    return merged.accessToken;
  } catch {
    localStorage.removeItem(STORAGE.tokens);
    return null;
  }
}

/**
 * Reads the profile out of the id token.
 *
 * Decoded without verifying the signature, which is safe **only** because nothing is authorized on
 * it: this supplies a name to greet someone by. Every actual permission is decided server-side by the
 * authorizer against the access token. If anything in this app ever starts making a decision from
 * these values, it needs to ask the API instead.
 */
export function profileFrom(tokens: Tokens): Profile | null {
  const payload = tokens.idToken.split(".")[1];
  if (!payload) return null;
  try {
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json) as Record<string, unknown>;
    const sub = typeof claims.sub === "string" ? claims.sub : null;
    if (!sub) return null;
    return {
      sub,
      email: typeof claims.email === "string" ? claims.email : undefined,
      name: typeof claims.name === "string" ? claims.name : undefined,
    };
  } catch {
    return null;
  }
}

// --- Token endpoint ------------------------------------------------------------------------------

async function exchange(body: Record<string, string>): Promise<Tokens> {
  const response = await fetch(`https://${config.cognito.hostedUiDomain}/oauth2/token`, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams(body).toString(),
  });
  if (!response.ok) {
    const detail = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(detail.error ?? `Sign-in failed (${response.status})`);
  }
  const payload = (await response.json()) as {
    access_token: string;
    id_token: string;
    refresh_token?: string;
    expires_in: number;
  };
  return {
    accessToken: payload.access_token,
    idToken: payload.id_token,
    refreshToken: payload.refresh_token,
    expiresAt: Date.now() + payload.expires_in * 1000,
  };
}

function store(tokens: Tokens): void {
  localStorage.setItem(STORAGE.tokens, JSON.stringify(tokens));
}

function clearUrl(): void {
  // Keeps the hash: it carries which screen to restore (see `readLocation` in App.tsx), and the
  // sign-in redirect is exactly the load where losing it is most noticeable.
  window.history.replaceState({}, "", window.location.pathname + window.location.hash);
}
